import { create } from 'zustand'
import type { WebSocketStore, EventMessage } from '@/types'

let ws: WebSocket | null = null
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null

export const useWebSocketStore = create<WebSocketStore>((set, get) => ({
  connected: false,
  connecting: false,
  messages: [],

  connect: () => {
    const state = get()
    if (state.connected || state.connecting) return

    set({ connecting: true })

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.host}/ws`

    try {
      ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log('WebSocket connected')
        set({ connected: true, connecting: false })
        
        // Send initial ping
        ws?.send(JSON.stringify({ type: 'Ping' }))
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          
          if (message.type === 'Event') {
            const eventMessage = message.data as EventMessage
            set((state) => ({
              messages: [...state.messages.slice(-99), eventMessage],
            }))
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      ws.onclose = () => {
        console.log('WebSocket disconnected')
        set({ connected: false, connecting: false })
        
        // Attempt to reconnect after 5 seconds
        reconnectTimeout = setTimeout(() => {
          get().connect()
        }, 5000)
      }
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      set({ connecting: false })
    }
  },

  disconnect: () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }

    if (ws) {
      ws.close()
      ws = null
    }

    set({ connected: false, connecting: false })
  },

  subscribe: (channels: string[]) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'Subscribe',
        data: { channels },
      }))
    }
  },

  unsubscribe: (channels: string[]) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'Unsubscribe',
        data: { channels },
      }))
    }
  },
}))